import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";

// Error boundary for DOMExceptions and other errors
window.addEventListener('error', (event) => {
  console.warn('Caught error:', event.error);
  if (event.error instanceof DOMException) {
    console.warn('DOMException caught, this might be a Vite HMR issue');
  }
});

window.addEventListener('unhandledrejection', (event) => {
  console.warn('Unhandled promise rejection:', event.reason);
});

// Register service worker for PWA functionality (production only)
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    if (import.meta.env.PROD) {
      // Clear old caches to fix CSP header caching issue (one-time fix)
      if ('caches' in window && !sessionStorage.getItem('caches-cleared-v4')) {
        console.log('Clearing old caches to fix CSP issues...');
        caches.keys().then(cacheNames => {
          return Promise.all(
            cacheNames.filter(name => name !== 'snugglecrafters-v4-no-csp').map(cacheName => {
              console.log('Deleting old cache:', cacheName);
              return caches.delete(cacheName);
            })
          );
        }).then(() => {
          console.log('Old caches cleared');
          sessionStorage.setItem('caches-cleared-v4', 'true');
        });
      }
      
      // Register service worker with automatic updates for production
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('SW registered: ', registration);
          
          // Check for updates immediately
          registration.update();
          
          // Handle waiting service worker (new version available)
          if (registration.waiting) {
            console.log('New SW version available, activating...');
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
          
          // Listen for new service worker installations
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  console.log('New SW installed, activating...');
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                }
              });
            }
          });
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError);
        });
      
      // Listen for service worker controller changes (new version activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('SW controller changed, reloading page...');
        // Reload once to get the fresh HTML from the new service worker
        if (!window.location.search.includes('sw-updated')) {
          const url = new URL(window.location.href);
          url.searchParams.set('sw-updated', '1');
          window.location.href = url.href;
        }
      });
    } else if (import.meta.env.DEV) {
      // In development, aggressively clear all caches and service workers
      console.log('Development mode: clearing all caches and service workers');
      
      // Clear all caches first
      if ('caches' in window) {
        caches.keys().then(async (cacheNames) => {
          console.log('Found caches:', cacheNames);
          for (const cacheName of cacheNames) {
            console.log('Clearing cache:', cacheName);
            await caches.delete(cacheName);
          }
          console.log('All caches cleared');
        }).catch(err => console.warn('Cache clearing error:', err));
      }
      
      // Unregister all service workers
      navigator.serviceWorker.getRegistrations().then(async (registrations) => {
        console.log('Found service workers:', registrations.length);
        for (const registration of registrations) {
          console.log('Unregistering SW:', registration.scope);
          await registration.unregister();
        }
        console.log('All service workers unregistered');
      }).catch(err => console.warn('SW unregistration error:', err));
      
      // Force reload if there were cached assets
      setTimeout(() => {
        if (window.location.search.indexOf('sw-cleared') === -1) {
          console.log('Reloading to ensure clean state');
          const url = new URL(window.location.href);
          url.searchParams.set('sw-cleared', '1');
          window.location.href = url.href;
        }
      }, 1000);
    }
  });
}

// Safe root creation with error handling
try {
  const rootElement = document.getElementById("root");
  if (rootElement) {
    const root = createRoot(rootElement);
    root.render(<App />);
  } else {
    console.error('Root element not found');
  }
} catch (error) {
  console.error('Error creating React root:', error);
}
